import { ComparisonCardContainer, StyledButton, StyledContainer } from './styles';
import { useTheme } from '@mui/material/styles';
import { Box, CircularProgress, SvgIconProps, useMediaQuery } from '@mui/material';
import { Flex } from '@/styles/globalStyles';
import { Text } from '@/components/atoms/text';
import { ElementType, ReactNode } from 'react';
import { Desktop } from '../viewport/desktop';
import { Mobile } from '../viewport/mobile';
import { SparklesIcon } from '@/components/atoms/customIcons/sparklesIcon';
import { useTranslation } from '@/hooks/useTranslation';
import { SafeMarkdown } from '@/components/atoms/markdown/safe';
import { MenuButton, MenuButtonOption } from '../button/menu';
import { FileDownloadOutlined } from '@mui/icons-material';
import { DownloadUsage } from '@/graphql/types/downloads';

interface ComparisonCardProps {
  title: string;
  buttons?: {
    text: string;
    icon: ElementType<SvgIconProps>;
    loading?: boolean;
    variant: 'contained' | 'outlined' | 'text';
    downloadOptions?: MenuButtonOption[];
    onClick?: () => void;
  }[];
  onInsight?: () => void;
  hasComparison?: boolean;
  children: ReactNode;
  loadingPDF?: boolean;
  loadingXLSX?: boolean;
  downloadUsage?: DownloadUsage;
  limitReachedMessage?: string;
}

export const ComparisonCard = ({
  title,
  buttons,
  onInsight,
  hasComparison,
  children,
  loadingPDF,
  loadingXLSX,
  downloadUsage,
  limitReachedMessage,
}: ComparisonCardProps) => {
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { t } = useTranslation();

  const Buttons = () => {
    return (
      <StyledContainer>
        {buttons?.map(({ text, icon: Icon, onClick, variant, downloadOptions, loading }, index) => {
          if (downloadOptions && downloadOptions.length > 0) {
            return (
              <MenuButton
                key={index}
                options={downloadOptions}
                icon={FileDownloadOutlined}
                label={t('button.button.export')}
                loading={loadingPDF || loadingXLSX}
                variant="outlined"
                downloadUsage={downloadUsage}
                limitReachedMessage={limitReachedMessage}
              />
            );
          }

          return (
            <StyledButton
              key={index}
              variant={variant}
              startIcon={<Icon color="inherit" />}
              onClick={onClick}
            >
              {loading ? (
                <Box sx={{ width: '118.92px' }}>
                  <CircularProgress sx={{ color: 'text.primary' }} size={18} />
                </Box>
              ) : (
                <SafeMarkdown text={text} />
              )}
            </StyledButton>
          );
        })}
      </StyledContainer>
    );
  };

  return (
    <ComparisonCardContainer variant={isMobile ? 'elevation' : 'outlined'} elevation={0}>
      <Flex
        justify="space-between"
        align={isMobile ? 'flex-start' : 'center'}
        gap={isMobile ? 2 : 0}
        fullWidth
      >
        <Text variant={isMobile ? 'h6' : 'h5'}>
          <SafeMarkdown text={title} />
        </Text>

        <Desktop>
          <Buttons />
        </Desktop>
      </Flex>

      <>
        {children}

        {onInsight && (
          <Mobile>
            <StyledButton
              variant="outlined"
              startIcon={<SparklesIcon color="inherit" />}
              onClick={onInsight}
              fullWidth
            >
              <SafeMarkdown text={t('button.button.insights')} />
            </StyledButton>
          </Mobile>
        )}

        {hasComparison && (
          <Mobile>
            <Flex fullWidth>
              <Buttons />
            </Flex>
          </Mobile>
        )}
      </>
    </ComparisonCardContainer>
  );
};
